import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { productData } from '../products/productData';
import ProductCardMedia from '../products/ProductCardMedia';
import '../../styles/pages/SitePages.css';
import AboutUsPage from './AboutUsPage';

const allProducts = Object.values(productData);

const faqItems = [
  {
    question: 'Do you offer free quotes?',
    answer: 'Yes. Use the contact form or call/text us and we will help schedule a free consultation.',
  },
  {
    question: 'Can I browse products by category?',
    answer: 'Yes. Use the Blinds, Shutters, and Shades pages to narrow the product list before opening a specific product.',
  },
  {
    question: 'Do you support motorized options?',
    answer: 'Several product pages include operating system details, including motorized and cordless options where available.',
  },
  {
    question: 'Where are you located?',
    answer: 'We are located in Kennesaw, Georgia. The support page includes the full address and map link.',
  },
];

function PageHero({ eyebrow, title, description, children }) {
  return (
    <section className="route-hero">
      <div className="container route-hero-panel">
        <p className="route-eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p className="route-description">{description}</p>
        {children ? <div className="route-actions">{children}</div> : null}
      </div>
    </section>
  );
}

function ProductsRoutePage() {
  return (
    <>
      <PageHero
        eyebrow="Browse everything"
        title="Products & Services"
        description="Start with the full collection, then move into the product page that matches your room, style, and light-control needs."
      >
        <Link to="/blinds" className="btn btn-primary">Blinds</Link>
        <Link to="/shutters" className="btn btn-primary">Shutters</Link>
        <Link to="/shades" className="btn btn-primary">Shades</Link>
      </PageHero>

      <section className="route-content-section">
        <div className="container">
          <div className="route-product-grid">
            {allProducts.map((product, index) => (
              <Link
                key={product.id}
                to={`/product/${product.id}`}
                className="product-card route-product-card"
              >
                <ProductCardMedia src={product.images?.[0]} index={index} />
                <h3>{product.name}</h3>
                <p>{product.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function AboutRoutePage() {
  return <AboutUsPage />;
}

function FaqRoutePage() {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaqIndex((currentIndex) => (currentIndex === index ? null : index));
  };

  return (
    <>
      <PageHero
        eyebrow="Questions answered"
        title="Frequently Asked Questions"
        description="A quick list of the most common questions about quotes, categories, and product options."
      >
        <Link to="/support" className="btn btn-primary">Support</Link>
        <Link to="/contact" className="btn btn-primary">Contact</Link>
      </PageHero>

      <section className="route-content-section">
        <div className="container">
          <div className="route-faq-list">
            {faqItems.map((item, index) => {
              const isOpen = openFaqIndex === index;

              return (
                <article key={item.question} className="route-faq-item">
                  <button
                    type="button"
                    className="route-faq-trigger"
                    onClick={() => toggleFaq(index)}
                    aria-expanded={isOpen}
                  >
                    <span>{item.question}</span>
                    <span className="route-faq-icon" aria-hidden="true">
                      {isOpen ? '−' : '+'}
                    </span>
                  </button>
                  <div className={`route-faq-answer ${isOpen ? 'is-open' : ''}`}>
                    <p>{item.answer}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}

function CategoryRoutePage({ category, title, description }) {
  const products = useMemo(
    () => Object.values(productData).filter((product) => product.category === category),
    [category],
  );

  return (
    <>
      <PageHero
        eyebrow={category}
        title={title}
        description={description}
      >
        <Link to="/products" className="btn btn-primary">All products</Link>
        <Link to="/contact" className="btn btn-primary">Request quote</Link>
      </PageHero>

      <section className="route-content-section">
        <div className="container">
          <div className="route-product-grid">
            {products.map((product, index) => (
              <Link
                key={product.id}
                to={`/product/${product.id}`}
                className="product-card route-product-card"
              >
                <ProductCardMedia src={product.images?.[0]} index={index} />
                <h3>{product.name}</h3>
                <p>{product.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function SupportRoutePage() {
  const supportLinks = [
    {
      title: 'Contact us',
      description: 'Send a message, request a quote, or ask about measurements and installation.',
      to: '/contact',
    },
    {
      title: 'FAQ',
      description: 'Quick answers about quotes, product categories, and available options.',
      to: '/faq',
    },
    {
      title: 'About Sonny Blinds',
      description: 'Learn about our team, experience, and approach to custom window treatments.',
      to: '/about',
    },
    {
      title: 'Browse products',
      description: 'Explore blinds, shutters, and shades to find the right fit for your space.',
      to: '/products',
    },
  ];

  return (
    <>
      <PageHero
        eyebrow="We are here to help"
        title="Customer Support"
        description="Find the fastest way to reach us, review common questions, or request a free quote."
      >
        <a href="tel:7705901666" className="btn btn-primary">Call now</a>
        <Link to="/contact" className="btn btn-primary">Request a quote</Link>
      </PageHero>

      <section className="route-content-section">
        <div className="container">
          <div className="route-support-grid">
            {supportLinks.map((item) => (
              <Link key={item.title} to={item.to} className="route-support-card">
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </Link>
            ))}
          </div>

          <aside className="contact-route-card contact-route-details" style={{ marginTop: '1.5rem' }}>
            <h2>Visit or reach us directly</h2>
            <p className="contact-route-intro">
              Monday – Friday: 8am – 5pm (by appointment)
            </p>
            <div className="contact-route-links">
              <a href="tel:7705901666" className="contact-route-link">
                <span>Call us</span>
                <strong>+1 (770) 590-1666</strong>
              </a>
              <a href="sms:+14047033838" className="contact-route-link">
                <span>Text us</span>
                <strong>+1 (404) 703-3838</strong>
              </a>
              <a href="mailto:sonnyblindpromo@gmail.com" className="contact-route-link">
                <span>Email us</span>
                <strong>sonnyblindpromo@gmail.com</strong>
              </a>
              <a
                href="https://www.google.com/maps/place/1680+Roberts+Blvd+%23403,+Kennesaw,+GA+30144/@34.0047566,-84.5979895,17z/data=!3m1!4b1!4m5!3m4!1s0x88f5156f7c9e03cd:0xd95e69e2c2c31e24!8m2!3d34.0047566!4d-84.5954146?entry=ttu&g_ep=EgoyMDI2MDMwOS4wIKXMDSoASAFQAw%3D%3D"
                className="contact-route-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>Visit us</span>
                <strong>1680 Roberts Blvd NW Ste 403, Kennesaw, GA 30144</strong>
              </a>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}

export {
  AboutRoutePage,
  CategoryRoutePage,
  FaqRoutePage,
  ProductsRoutePage,
  SupportRoutePage,
};